import { Grid, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { format, isValid } from "date-fns";
import { useCallback, useEffect } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  ValidateResult,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../../common/helpers/isEmpty";
import { SalaryFormValues } from "../../../../hooks/useTesSalaryComponents";
import SalaryComponents from "../../editForms/salaries/SalaryComponents";
import { SelectedSalaryRow } from "../types";
import { formatSalaryComponents } from "../../../../common/utils/formatSalaryComponents";
import { SalaryType } from "../../../orders/types/salaryType";
import {
  withFullSalaryMode,
  withInfoOnlyMode,
} from "../../editForms/salaries/tesSalaryTableFormUtils";
import {
  isoFormat,
  leadingZerosDateFormat,
} from "../../../../common/helpers/formatDateTime";

type Props = {
  selectedSalaryRow: SelectedSalaryRow | null;
  tesId: string;
  workTitleId: string;
  onClose: () => void;
  onAppend: (data: SalaryFormValues) => void;
  onUpdate: (data: SalaryFormValues, index: number) => void;
  salaryType: SalaryType;
};

const SalaryRowForm = ({
  selectedSalaryRow,
  tesId,
  workTitleId,
  onClose,
  onUpdate,
  onAppend,
  salaryType,
}: Props) => {
  const { t } = useTranslation();

  const { watch: watchParent } = useFormContext();
  const fields: SalaryFormValues[] = watchParent("salaryRows");

  const { status } = useAppSelector((state) => state.employment);
  const isLoading = status === "pending";

  const methods = useForm({
    defaultValues: {
      from: selectedSalaryRow?.components.from || new Date(),
      components: selectedSalaryRow?.components || {},
      totalSalary: selectedSalaryRow?.totalSalary || "",
      tesSalaryTableSelection:
        selectedSalaryRow?.tesSalaryTableSelection || null,
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid: isValidForm, dirtyFields },
  } = methods;

  useEffect(() => {
    reset({
      components: selectedSalaryRow?.components || {},
      from: selectedSalaryRow?.components.from
        ? new Date(selectedSalaryRow?.components.from)
        : new Date(),
      totalSalary: selectedSalaryRow?.totalSalary || "",
      tesSalaryTableSelection:
        selectedSalaryRow?.tesSalaryTableSelection || null,
    });
  }, [selectedSalaryRow, reset]);

  const validateDate = (date: Date): ValidateResult => {
    if (!isValid(date)) return false;

    const isExistingDate = fields.some(
      (field) => field.components.from === format(date, isoFormat),
    );

    // allow editing only from edit dialog
    // selectedSalaryRow.isCopy = add new with prefilled values
    if (isExistingDate && (!selectedSalaryRow || selectedSalaryRow.isCopy)) {
      const errorMessage = t("talentProfile:existingSalaryRowError");
      return errorMessage;
    }

    const formattedDate = format(date, isoFormat);
    const dateParts = formattedDate.split("-");
    const yearContainsLeadingZeros = /^0+/.test(dateParts?.[0]);

    return (
      dateParts && formattedDate.length === 10 && !yearContainsLeadingZeros
    );
  };

  const onCancel = () => onClose();

  const onSave = (data: FieldValues) => {
    const { from, ...rest } = data.components;

    const formattedData: SalaryFormValues = {
      components: {
        ...formatSalaryComponents(rest),
        from: format(data.from, isoFormat),
      },
      totalSalary: data.totalSalary,
      tesSalaryTableSelection: data.tesSalaryTableSelection || null,
    };

    const existingDateIndex = fields.findIndex(
      (row) => row.components.from === formattedData.components.from,
    );

    // selectedSalaryRow = edit
    // selectedSalaryRow.copy = add new with prefilled values
    if (selectedSalaryRow && !selectedSalaryRow.isCopy) {
      onUpdate(formattedData, existingDateIndex);
    } else {
      onAppend(formattedData);
    }
  };

  const validFrom = watch("from");
  const componentValues = watch("components");
  const tesPickerDisabled = Object.keys(componentValues || {}).length === 0;
  const tesPickerDate =
    validFrom instanceof Date
      ? validFrom
      : validFrom
        ? new Date(validFrom)
        : null;
  const handleAvailabilityChange = useCallback(
    (isAvailable: boolean) => {
      if (!isAvailable && methods.getValues("tesSalaryTableSelection")) {
        methods.setValue("tesSalaryTableSelection", null, {
          shouldDirty: true,
        });
      }
    },
    [methods],
  );

  const saveDisabled = isEmpty(dirtyFields) || !isEmpty(errors) || !isValidForm;

  const title = selectedSalaryRow
    ? selectedSalaryRow.isCopy
      ? t("talentProfile:copySalaryRow")
      : t("talentProfile:editSalaryRow")
    : t("talentProfile:addSalaryRow");

  return (
    <DialogContentWrapper
      title={title}
      onCancel={onCancel}
      onSave={handleSubmit(onSave)}
      saveDisabled={saveDisabled}
    >
      <OverlaySpinner isLoading={isLoading} />
      <FormProvider {...methods}>
        <Grid item xs={4} textAlign="end" mt="7px">
          <Typography variant="secondary">{t("common:start")}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Controller
            name="from"
            control={control}
            rules={{
              required: true,
              validate: (date) => validateDate(date as Date),
            }}
            render={({ field }) => (
              <DesktopDatePicker
                {...field}
                disabled={!!selectedSalaryRow && !selectedSalaryRow.isCopy}
                value={new Date(field.value)}
                defaultValue={new Date()}
                format={leadingZerosDateFormat}
                onChange={(newDate: Date | null) => field.onChange(newDate)}
                slotProps={{
                  textField: {
                    sx: { maxWidth: "135px" },
                    size: "small",
                    fullWidth: true,
                    error: !!errors.from,
                  },
                }}
              />
            )}
          />
          <Grid item xs={12}>
            <Typography
              mt="3px"
              color="error"
              fontSize="12px"
              letterSpacing="0.03333em"
            >
              {errors.from?.message}
            </Typography>
          </Grid>
        </Grid>
        <SalaryComponents
          tesId={tesId}
          defaultValues={selectedSalaryRow?.components}
          validFrom={new Date(validFrom)}
          salaryType={salaryType}
          tesSalaryTableProps={{
            disabled: tesPickerDisabled,
            tesId,
            workTitleId,
            date: tesPickerDate,
            value: watch("tesSalaryTableSelection") || null,
            onAvailabilityChange: handleAvailabilityChange,
            onChange: ({ selection }) => {
              methods.setValue(
                "tesSalaryTableSelection",
                withFullSalaryMode(selection),
                {
                  shouldDirty: true,
                },
              );
            },
          }}
          hasTesSalaryTableSelection={!!watch("tesSalaryTableSelection")}
          onManualBaseComponentChange={() => {
            const selection = methods.getValues("tesSalaryTableSelection");
            if (!selection) return;
            methods.setValue(
              "tesSalaryTableSelection",
              withInfoOnlyMode(selection),
              {
                shouldDirty: true,
              },
            );
            methods.trigger("components");
          }}
        />
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default SalaryRowForm;
